import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import {
    Users, Package, Store, ShieldCheck,
    ChevronRight, AlertCircle
} from "lucide-react";

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
                headers: { Authorization: token }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error("คุณไม่มีสิทธิ์เข้าถึงหน้านี้");
                }
                throw new Error("Failed to fetch stats");
            }

            const data = await response.json();
            setStats(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">กำลังโหลด...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center bg-white p-8 rounded-xl shadow-sm">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
                    <p className="text-gray-500 mb-4">{error}</p>
                    <Link to="/home" className="text-red-600 hover:underline">กลับหน้าแรก</Link>
                </div>
            </div>
        );
    }

    const menuItems = [
        {
            title: "จัดการผู้ใช้งาน",
            description: "ดู แก้ไข หรือลบบัญชีผู้ใช้",
            icon: Users,
            link: "/admin/users",
            color: "bg-blue-500"
        },
        {
            title: "จัดการสินค้า",
            description: "ดู ซ่อน หรือลบสินค้า",
            icon: Package,
            link: "/admin/products",
            color: "bg-green-500"
        },
        {
            title: "อนุมัติผู้ขาย",
            description: "ตรวจสอบและอนุมัติคำขอเป็นผู้ขาย",
            icon: Store,
            link: "/admin/verify-shops",
            color: "bg-orange-500",
            badge: stats?.pendingSellers > 0 ? stats.pendingSellers : null
        }
    ];

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-black rounded-lg">
                        <ShieldCheck className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                        <p className="text-gray-500">ศูนย์ควบคุมระบบ</p>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">ผู้ใช้ทั้งหมด</p>
                                <p className="text-3xl font-bold text-gray-900">{stats?.totalUsers || 0}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Users className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">สินค้าทั้งหมด</p>
                                <p className="text-3xl font-bold text-gray-900">{stats?.totalProducts || 0}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-lg">
                                <Package className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">ผู้ขายทั้งหมด</p>
                                <p className="text-3xl font-bold text-gray-900">{stats?.totalSellers || 0}</p>
                            </div>
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <Store className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">รอการอนุมัติ</p>
                                <p className="text-3xl font-bold text-orange-600">{stats?.pendingSellers || 0}</p>
                            </div>
                            <div className="p-3 bg-orange-100 rounded-lg">
                                <ShieldCheck className="w-6 h-6 text-orange-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Menu Cards */}
                <h2 className="text-xl font-bold text-gray-900 mb-4">เมนูจัดการ</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {menuItems.map((item, index) => (
                        <Link
                            key={index}
                            to={item.link}
                            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all group"
                        >
                            <div className="flex items-start justify-between">
                                <div className={`p-3 ${item.color} rounded-lg mb-4`}>
                                    <item.icon className="w-6 h-6 text-white" />
                                </div>
                                {item.badge && (
                                    <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                                        {item.badge}
                                    </span>
                                )}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">
                                {item.title}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">{item.description}</p>
                            <div className="flex items-center text-red-600 text-sm font-medium">
                                <span>เข้าสู่เมนู</span>
                                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
